import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useLayoutEffect, useRef, useState } from "react";
import { configurationPatch } from "../../../shared/connectors/configurationPatch";
import { getTargetValue } from "../../../shared/connectors/getTargetValue";
import {
  listFetchErrorKey,
  listFetchStateKey,
  listRowKey,
  listRows,
} from "../../../shared/connectors/listRows";
import type {
  FieldTarget,
  ListField,
  ListFieldColumn,
} from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import {
  forgetConnectorDestinationProof,
  queueConnectorEdit,
  withConnectorDestinationProof,
} from "../../sealSecret";
import { sealSecretInput } from "./sealSecretInput";
import { Box } from "../Box";
import { Button } from "../Button";
import { Input } from "../Input";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import type { App } from "./Form";

const Rows = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const RemoveSpacer = styled("div")`
  width: 24px;
  flex-shrink: 0;
`;

const Actions = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PENDING_SAVE = "Please wait for the change to save.";

type Props = {
  item: ListField;
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
  saveErrors: ReadonlySet<string>;
  onFieldSaveStatus: (field: string, saved: boolean) => void;
};

export function ListFieldItem({
  item,
  app,
  disabled,
  onEdit,
  saveErrors,
  onFieldSaveStatus,
}: Props) {
  const z = useZero();
  const persisted = listRows(app.configuration, item.prefix);
  const [draftIds, setDraftIds] = useState<number[]>([]);

  // Late debounce flushes from Input fire against stale closures; route every
  // write through the latest app state so they land in the current store.
  const appRef = useRef(app);
  useLayoutEffect(() => {
    appRef.current = app;
  });

  const persistedIds = new Set(persisted.map((row) => row.id));
  const ids = [
    ...persisted.map((row) => row.id),
    ...draftIds.filter((id) => !persistedIds.has(id)),
  ];

  const statusKey = (field: string) => `list:${item.prefix}:${field}`;
  const save = (
    field: string,
    write: () => Promise<void>,
    input?: HTMLInputElement
  ) => {
    onEdit?.();
    input?.setCustomValidity(PENDING_SAVE);
    void queueConnectorEdit(app.tenantId, app.id, async () => {
      try {
        await write();
        input?.setCustomValidity("");
        onFieldSaveStatus(statusKey(field), true);
      } catch {
        if (
          input &&
          (!input.validity.customError ||
            input.validationMessage === PENDING_SAVE)
        )
          input.setCustomValidity("This change wasn’t saved. Try again.");
        onFieldSaveStatus(statusKey(field), false);
      }
    });
  };

  const persist = async (
    args: Parameters<typeof sharedMutators.updateConnectedApp>[0]
  ) => {
    const result = await z.mutate(sharedMutators.updateConnectedApp(args))
      .server;
    if (result.type !== "success") throw new Error();
  };

  const writeTarget = (
    target: FieldTarget,
    value: string,
    input: HTMLInputElement
  ) => {
    if (target.kind === "secrets") {
      if (!value) {
        input.setCustomValidity("");
        onFieldSaveStatus(statusKey(target.field), true);
        return;
      }
      save(
        target.field,
        async () => {
          const sealed = await sealSecretInput(input, value, {
            app: appRef.current,
            field: target.field,
          });
          if (sealed === undefined) throw new Error();
          await persist(
            await withConnectorDestinationProof(appRef.current, {
              secrets: { [target.field]: sealed },
            })
          );
        },
        input
      );
      return;
    }
    if (target.kind !== "configuration") return;
    save(
      target.field,
      async () => {
        await persist(
          await withConnectorDestinationProof(appRef.current, {
            configurationPatch: { [target.field]: value },
          })
        );
      },
      input
    );
  };

  const isPlain = (key: string) =>
    appRef.current.configuration[key] !== undefined;

  const writeRowCell = (
    id: number,
    column: ListFieldColumn,
    value: string,
    input: HTMLInputElement
  ) => {
    const key = listRowKey(item.prefix, id, column.field);
    const kind = column.secret && !isPlain(key) ? "secrets" : "configuration";
    return writeTarget({ kind, field: key }, value, input);
  };

  const toggleSecret = (
    id: number,
    column: ListFieldColumn,
    currentValue: string,
    input: HTMLInputElement
  ) => {
    const key = listRowKey(item.prefix, id, column.field);
    if (isPlain(key)) {
      save(
        key,
        async () => {
          const configuration = Object.fromEntries(
            Object.entries(appRef.current.configuration).filter(
              ([k]) => k !== key
            )
          );
          const sealed = currentValue
            ? await sealSecretInput(input, currentValue, {
                app: { ...appRef.current, configuration },
                field: key,
              })
            : "";
          if (sealed === undefined) throw new Error();
          await persist(
            await withConnectorDestinationProof(appRef.current, {
              configurationPatch: { [key]: null },
              secrets: { [key]: sealed },
            })
          );
        },
        input
      );
      return;
    }
    save(
      key,
      async () => {
        forgetConnectorDestinationProof(
          appRef.current.tenantId,
          appRef.current.id,
          key
        );
        await persist(
          await withConnectorDestinationProof(appRef.current, {
            configurationPatch: { [key]: currentValue },
            secrets: { [key]: "" },
          })
        );
      },
      input
    );
  };

  const removeRow = (id: number) => {
    setDraftIds((prev) => prev.filter((draftId) => draftId !== id));
    if (!persistedIds.has(id)) {
      onEdit?.();
      return;
    }
    save(`row:${id}`, async () => {
      for (const column of item.columns) {
        if (column.secret)
          forgetConnectorDestinationProof(
            appRef.current.tenantId,
            appRef.current.id,
            listRowKey(item.prefix, id, column.field)
          );
      }
      const rowPrefix = `${item.prefix}.${id}.`;
      await persist(
        await withConnectorDestinationProof(appRef.current, {
          configurationPatch: configurationPatch(
            appRef.current.configuration,
            Object.fromEntries(
              Object.entries(appRef.current.configuration).filter(
                ([key]) => !key.startsWith(rowPrefix)
              )
            )
          ),
          // Blank rather than orphan the secret: row ids can be reused, and a
          // stale value would silently revive under a future row.
          secrets: Object.fromEntries(
            item.columns
              .filter((column) => column.secret)
              .map((column) => [listRowKey(item.prefix, id, column.field), ""])
          ),
        })
      );
      for (const column of item.columns)
        onFieldSaveStatus(
          statusKey(listRowKey(item.prefix, id, column.field)),
          true
        );
    });
  };

  const addRow = () => {
    const nextId = ids.length ? Math.max(...ids) + 1 : 0;
    setDraftIds((prev) => [...prev, nextId]);
  };

  const fetchState = app.configuration[listFetchStateKey(item.prefix)];
  const fetching = fetchState === "fetching";
  const fetchError =
    fetchState === "error"
      ? app.configuration[listFetchErrorKey(item.prefix)]
      : null;

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <Rows>
        {item.primaryRow && (
          <Row>
            {item.columns.map((column) => {
              const cell = item.primaryRow![column.field];
              if (!cell) return null;
              const masked = cell.target.kind === "secrets";
              return (
                <Cell
                  key={column.field}
                  masked={masked}
                  value={getTargetValue(cell.target, app)}
                  showDots={masked && !app.fresh}
                  placeholder={cell.placeholder}
                  lock={masked ? "fixed" : "none"}
                  disabled={disabled}
                  onFinish={(value, input) =>
                    writeTarget(cell.target, value, input)
                  }
                />
              );
            })}
            {!disabled && <RemoveSpacer />}
          </Row>
        )}
        {ids.map((id) => (
          <Row key={id}>
            {item.columns.map((column) => {
              const key = listRowKey(item.prefix, id, column.field);
              const secretNow =
                !!column.secret && app.configuration[key] === undefined;
              return (
                <Cell
                  key={column.field}
                  masked={secretNow}
                  value={app.configuration[key]}
                  showDots={secretNow && persistedIds.has(id) && !app.fresh}
                  placeholder={
                    secretNow
                      ? (column.secretPlaceholder ?? column.placeholder)
                      : column.placeholder
                  }
                  lock={column.secret ? "toggle" : "none"}
                  disabled={disabled}
                  onFinish={(value, input) =>
                    writeRowCell(id, column, value, input)
                  }
                  onToggle={(currentValue, input) =>
                    toggleSecret(id, column, currentValue, input)
                  }
                />
              );
            })}
            {!disabled && (
              <Button
                type="button"
                variant="secondary"
                icon="x"
                onClick={() => removeRow(id)}
              />
            )}
          </Row>
        ))}
        {!disabled && (
          <Actions>
            <Button
              type="button"
              variant="secondary"
              icon="plus"
              onClick={addRow}
            >
              {item.addLabel}
            </Button>
            {item.fetch && (
              <Button
                type="button"
                variant="secondary"
                icon="refresh-cw"
                iconSpinning={fetching}
                onClick={() => {
                  onEdit?.();
                  z.mutate(
                    sharedMutators.refreshConnectorList({
                      connectedAppId: app.id,
                      prefix: item.prefix,
                    })
                  );
                }}
              >
                {fetching ? "Fetching…" : item.fetch.label}
              </Button>
            )}
          </Actions>
        )}
      </Rows>
      {fetchError && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-error)">
            {fetchError}
          </Text>
        </Box>
      )}
      {[...saveErrors].some((key) =>
        key.startsWith(`list:${item.prefix}:`)
      ) && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-error)">
            A list change wasn’t saved. Try again.
          </Text>
        </Box>
      )}
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {item.helperText}
          </Text>
        </Box>
      )}
    </div>
  );
}

function Cell({
  masked,
  value,
  showDots,
  placeholder,
  lock,
  disabled,
  onFinish,
  onToggle,
}: {
  masked: boolean;
  value: string | undefined;
  showDots: boolean;
  placeholder?: string;
  lock: "none" | "fixed" | "toggle";
  disabled?: boolean;
  onFinish: (value: string, input: HTMLInputElement) => void;
  onToggle?: (currentValue: string, input: HTMLInputElement) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleToggle = () => {
    const input = inputRef.current;
    if (!input || !onToggle) return;
    // A stored-but-unread secret shows as the dots placeholder over an empty
    // value, so reading input.value carries the typed value or starts blank.
    onToggle(input.value, input);
  };

  return (
    <>
      <Input
        ref={inputRef}
        type="text"
        secret={masked}
        secretIsSet={showDots}
        defaultValue={value}
        placeholder={placeholder}
        style={{ flex: 1, minWidth: 0 }}
        onFinishChange={(value) => {
          if (inputRef.current) onFinish(value, inputRef.current);
        }}
        readOnly={disabled}
      />
      {lock === "fixed" && (
        <Tooltip content="Encrypted secret">
          <Button
            type="button"
            variant="secondary"
            icon="lock-filled"
            disabled
          />
        </Tooltip>
      )}
      {lock === "toggle" && !disabled && (
        <Tooltip content={masked ? "Encrypted secret" : "Non-secret value"}>
          <Button
            type="button"
            variant="secondary"
            icon={masked ? "lock-filled" : "lock"}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleToggle}
          />
        </Tooltip>
      )}
    </>
  );
}
